import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronRight, Megaphone, Newspaper, Cog } from 'lucide-react';
import {
  AdminNotification, NotificationCategory, NotificationPriority, NotificationStatus,
  LIVE_NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS, getTypePresentation, listAdminNotifications,
} from '../../composables/notificationAPI';

const STATUS_COLORS: Record<NotificationStatus, string> = {
  DRAFT: 'var(--secondary-text-color)',
  SCHEDULED: 'var(--yellow-text-color)',
  PUBLISHED: 'var(--green-text-color)',
  CANCELLED: 'var(--red-text-color)',
  EXPIRED: 'var(--secondary-text-color)',
};

const STATUS_LABELS_TH: Record<NotificationStatus, string> = {
  DRAFT: 'ฉบับร่าง',
  SCHEDULED: 'ตั้งเวลาไว้',
  PUBLISHED: 'เผยแพร่แล้ว',
  CANCELLED: 'ยกเลิกแล้ว',
  EXPIRED: 'หมดอายุ',
};

const CATEGORY_ICONS: Partial<Record<NotificationCategory, React.ComponentType<{ size?: number }>>> = {
  ARTICLE: Newspaper,
  ANNOUNCEMENT: Megaphone,
  SYSTEM: Cog,
};

const selectCls = 'text-sm rounded-lg px-3 py-2.5';
const selectStyle = { backgroundColor: 'var(--bg-item)', color: 'var(--text-color)' } as const;

const AdminNotificationList: React.FC<{ onSelect: (id: string) => void }> = ({ onSelect }) => {
  const [items, setItems] = useState<AdminNotification[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<NotificationStatus | ''>('');
  const [category, setCategory] = useState<NotificationCategory | ''>('');
  const [priority, setPriority] = useState<NotificationPriority | ''>('');

  const load = useCallback(async (reset: boolean, cursorValue?: string | null) => {
    setLoading(true);
    try {
      const data = await listAdminNotifications({ status: status || undefined, category: category || undefined, priority: priority || undefined, cursor: reset ? undefined : cursorValue });
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
      setCursor(data.nextCursor);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [status, category, priority]);

  useEffect(() => { void load(true); }, [load]);

  return (
    <div>
      <div className="rounded-2xl p-5 mb-4" style={{ backgroundColor: 'var(--component-bg-color)' }}>
        <div className="flex flex-wrap gap-2">
          <select className={selectCls} style={selectStyle} value={status} onChange={(e) => setStatus(e.target.value as NotificationStatus | '')}>
            <option value="">ทุกสถานะ</option>
            {(['DRAFT', 'SCHEDULED', 'PUBLISHED', 'CANCELLED', 'EXPIRED'] as NotificationStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABELS_TH[s]}</option>)}
          </select>
          <select className={selectCls} style={selectStyle} value={category} onChange={(e) => setCategory(e.target.value as NotificationCategory | '')}>
            <option value="">ทุกหมวด</option>
            {LIVE_NOTIFICATION_CATEGORIES.map((c) => <option key={c} value={c}>{NOTIFICATION_CATEGORY_LABELS[c]}</option>)}
          </select>
          <select className={selectCls} style={selectStyle} value={priority} onChange={(e) => setPriority(e.target.value as NotificationPriority | '')}>
            <option value="">ทุกความสำคัญ</option>
            {(['LOW', 'NORMAL', 'HIGH', 'CRITICAL'] as NotificationPriority[]).map((p) => <option key={p}>{p}</option>)}
          </select>
          <button onClick={() => load(true)} className="ml-auto text-sm font-semibold px-4 py-2.5 rounded-lg" style={{ backgroundColor: 'var(--button-create-bg)', color: 'var(--button-text-color)' }}>
            รีเฟรช
          </button>
        </div>
      </div>

      {items.length === 0 && !loading ? (
        <div className="rounded-2xl py-16 text-center text-sm opacity-60" style={{ backgroundColor: 'var(--component-bg-color)' }}>ยังไม่มีประกาศตามเงื่อนไขนี้</div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((n) => {
            const Icon = CATEGORY_ICONS[n.category] ?? Megaphone;
            return (
              <button
                key={n.id}
                onClick={() => onSelect(n.id)}
                className="text-left rounded-2xl p-5 flex flex-col gap-3 transition-transform hover:-translate-y-0.5 hover:shadow-xl"
                style={{ backgroundColor: 'var(--component-bg-color)', border: '1px solid var(--bg-item)' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-xs font-bold px-2.5 py-1 rounded-full"
                    style={{ color: STATUS_COLORS[n.status], backgroundColor: 'var(--bg-item)' }}
                  >
                    {STATUS_LABELS_TH[n.status]}
                  </span>
                  <ChevronRight size={18} className="opacity-40" />
                </div>

                <div className="flex items-start gap-3">
                  {n.image ? (
                    <img src={n.image} alt="" className="w-10 h-10 rounded-xl object-cover shrink-0" style={{ backgroundColor: 'var(--bg-item)' }} />
                  ) : (
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--bg-item)', color: 'var(--green-text-color)' }}>
                      <Icon size={18} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-base font-bold leading-snug line-clamp-2">{n.title}</div>
                    <div className="text-xs opacity-60 mt-1">{NOTIFICATION_CATEGORY_LABELS[n.category] || n.category} · {getTypePresentation(n.category, n.type).typeLabel} · {n.priority || 'NORMAL'}</div>
                  </div>
                </div>

                <div className="text-[11px] opacity-40 truncate font-mono">{n.id}</div>
              </button>
            );
          })}
        </div>
      )}

      {cursor && (
        <button disabled={loading} onClick={() => load(false, cursor)} className="w-full mt-4 py-3 rounded-xl text-sm font-semibold opacity-80 hover:opacity-100" style={{ backgroundColor: 'var(--component-bg-color)' }}>
          {loading ? 'กำลังโหลด...' : 'โหลดเพิ่มเติม'}
        </button>
      )}
    </div>
  );
};

export default AdminNotificationList;
